/**
 * What: Shared card grid for App Home surfaces — a row of cards that can mix widths (one card
 * spanning 2 tracks next to two 1-track cards) or run uniform at a wider/narrower track size.
 * Not masonry: rows stay a fixed height, only column widths vary. CSS Grid keeps the layout
 * consistent in Shopify Admin and in the local Ladle stories, where App Bridge custom elements
 * do not provide their production layout behavior.
 *
 */
import type { ComponentProps, ReactNode } from 'react'

/** Row of cards. `minColumnWidth` sets the base track size — auto-fill keeps lone cards the same
 * width as their siblings instead of stretching them across empty tracks. */
export function CardGrid({
  minColumnWidth = 240,
  maxColumns,
  gap = 16,
  display = 'auto',
  accessibilityLabel,
  children,
}: {
  minColumnWidth?: number
  maxColumns?: number
  gap?: number
  display?: ComponentProps<'s-grid'>['display']
  accessibilityLabel?: string
  children: ReactNode
}) {
  return (
    <div
      role="group"
      aria-label={accessibilityLabel}
      style={{
        display: display === 'none' ? 'none' : 'grid',
        gridTemplateColumns: maxColumns
          ? `repeat(auto-fill, minmax(max(${minColumnWidth}px, calc((100% - ${gap * (maxColumns - 1) + 1}px) / ${maxColumns})), 1fr))`
          : `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
        gap,
      }}
    >
      {children}
    </div>
  )
}

/** One card's slot in a `CardGrid`. `span` is how many base tracks wide it is — 1 (default) is a
 * normal card, 2 makes it twice as wide as its siblings in the same row. */
export function CardGridItem({ span = 1, children }: { span?: number; children: ReactNode }) {
  return <div style={{ gridColumn: span > 1 ? `span ${span}` : 'auto', minWidth: 0 }}>{children}</div>
}
